import { i18n } from "@/plugin/vue-i18n";
import type { AssetType, BuilderSection, RuleResult } from "./types";

/**
 * Panel grouping. Each builder names its groups differently, and quizzes group
 * by page rather than by surface, so a rule's `section` is mapped per asset
 * type rather than rendered directly.
 *
 * Quizzes have no Payments group at all — the payments surface is removed from
 * the quiz builder, so no payment rule can fire there.
 */
export type GroupId =
  | "fields"
  | "questions"
  | "questionsPage"
  | "resultsPage"
  | "payments"
  | "settings";

const FORM_GROUPS: Record<BuilderSection, GroupId> = {
  fields: "fields",
  questions: "fields",
  questionsPage: "fields",
  resultsPage: "settings",
  payments: "payments",
  logic: "settings",
  settings: "settings",
};

const SURVEY_GROUPS: Record<BuilderSection, GroupId> = {
  fields: "questions",
  questions: "questions",
  questionsPage: "questions",
  resultsPage: "settings",
  payments: "payments",
  logic: "settings",
  settings: "settings",
};

const QUIZ_GROUPS: Record<BuilderSection, GroupId> = {
  fields: "questionsPage",
  questions: "questionsPage",
  questionsPage: "questionsPage",
  resultsPage: "resultsPage",
  payments: "settings",
  logic: "settings",
  settings: "settings",
};

const BY_ASSET: Record<AssetType, Record<BuilderSection, GroupId>> = {
  form: FORM_GROUPS,
  survey: SURVEY_GROUPS,
  quiz: QUIZ_GROUPS,
};

/** Render order. Groups with no findings are not shown. */
const ORDER: GroupId[] = [
  "fields",
  "questions",
  "questionsPage",
  "resultsPage",
  "payments",
  "settings",
];

export const groupIdFor = (
  assetType: AssetType,
  section: BuilderSection
): GroupId => BY_ASSET[assetType][section] ?? "settings";

/** Literal keys: the repo forbids building locale keys at runtime. */
export const groupLabel = (groupId: GroupId): string => {
  switch (groupId) {
    case "fields":
      return i18n.global.t("builderValidation.groups.fields") as string;
    case "questions":
      return i18n.global.t("builderValidation.groups.questions") as string;
    case "questionsPage":
      return i18n.global.t("builderValidation.groups.questionsPage") as string;
    case "resultsPage":
      return i18n.global.t("builderValidation.groups.resultsPage") as string;
    case "payments":
      return i18n.global.t("builderValidation.groups.payments") as string;
    default:
      return i18n.global.t("builderValidation.groups.settings") as string;
  }
};

export interface RuleGroup {
  id: GroupId;
  label: string;
  results: RuleResult[];
}

export const groupResults = (
  assetType: AssetType,
  results: RuleResult[]
): RuleGroup[] => {
  const buckets = new Map<GroupId, RuleResult[]>();
  results.forEach((result) => {
    const groupId = groupIdFor(assetType, result.section);
    const bucket = buckets.get(groupId);
    if (bucket) bucket.push(result);
    else buckets.set(groupId, [result]);
  });

  return ORDER.filter((groupId) => buckets.has(groupId)).map((groupId) => ({
    id: groupId,
    label: groupLabel(groupId),
    results: buckets.get(groupId) ?? [],
  }));
};
